import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { MobileBottomNav } from './MobileBottomNav';
import { InstallPrompt } from './InstallPrompt';
import { InstallAppButton } from './InstallAppButton';
import {
  LayoutDashboard,
  DollarSign,
  Wrench,
  Calendar,
  Package,
  QrCode,
  MessageSquare,
  Bell,
  FileText,
  Vote,
  LogOut,
  Menu,
  X,
  Building2,
  Users,
  Receipt,
  Droplets,
  Inbox,
  FilePlus,
  Mail
} from 'lucide-react';

export const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getNavigationItems = () => {
    const items = [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['resident', 'owner', 'security', 'management'] }
    ];

    if (user?.role === 'resident') {
      items.push(
        { path: '/payments', label: 'Payments', icon: DollarSign },
        { path: '/invoices', label: 'My Invoices', icon: Receipt },
        { path: '/facilities', label: 'Facilities', icon: Calendar },
        { path: '/maintenance', label: 'Maintenance', icon: Wrench },
        { path: '/visitors', label: 'Visitor Passes', icon: QrCode },
        { path: '/parcels', label: 'Parcels', icon: Package },
        { path: '/announcements', label: 'Announcements', icon: Bell },
        { path: '/polls', label: 'Polls', icon: Vote },
        { path: '/chat', label: 'Chat with Management', icon: MessageSquare }
      );
    } else if (user?.role === 'management') {
      items.push(
        { path: '/users', label: 'User Management', icon: Users },
        { path: '/properties', label: 'Properties', icon: Building2 },
        // ── Billing section ───────────────────────────────────────────
        { section: 'Billing' },
        { path: '/prepare-bills', label: 'Prepare Bills', icon: FilePlus },
        { path: '/invoices', label: 'Invoices', icon: Receipt },
        { path: '/payment-approvals', label: 'Payments', icon: Inbox },
        { path: '/statements', label: 'Statements', icon: Mail },
        // ── Community & operations ────────────────────────────────────
        { section: 'Operations' },
        { path: '/facilities', label: 'Facility Bookings', icon: Calendar },
        { path: '/maintenance', label: 'Maintenance', icon: Wrench },
        { path: '/announcements', label: 'Announcements', icon: Bell },
        { path: '/polls', label: 'Polls', icon: Vote },
        { path: '/chat', label: 'Resident Messages', icon: MessageSquare },
        { path: '/water-tariff', label: 'Water Tariff', icon: Droplets },
        { path: '/reports', label: 'Reports', icon: FileText }
      );
    } else if (user?.role === 'security') {
      items.push(
        { path: '/visitors', label: 'Scan Visitor Pass', icon: QrCode },
        { path: '/parcels', label: 'Log Parcels', icon: Package }
      );
    } else if (user?.role === 'supervisor') {
      items.push(
        { path: '/maintenance', label: 'My Tickets', icon: Wrench }
      );
    } else if (user?.role === 'owner') {
      items.push(
        { path: '/payments', label: 'Payments', icon: DollarSign },
        { path: '/facilities', label: 'Facilities', icon: Calendar },
        { path: '/visitors', label: 'Visitor Passes', icon: QrCode },
        { path: '/reports', label: 'Reports', icon: FileText }
      );
    }

    return items;
  };

  const navItems = getNavigationItems();

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="layout-container">
      {/* Top Header - Mobile */}
      <div className="lg:hidden sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-[#e2e8f0] px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/assets/span-tower-logo.jpeg" alt="Span Tower" className="h-9 w-auto object-contain" />
            <div className="leading-tight">
              <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>My SPAN</p>
              <p className="text-[10px] text-[#64748b] italic">Your Home. Connected.</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="mobile-logout-button"
              className="h-11 w-11 text-[#64748b] hover:text-[#ef4444]"
              aria-label="Log off"
              title="Log off"
            >
              <LogOut className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-button"
              className="h-11 w-11"
              aria-label="Menu"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Sidebar - Desktop & Mobile */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-64 bg-white border-r border-[#e2e8f0] transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#e2e8f0]">
            <img
              src="/assets/span-tower-logo.jpeg"
              alt="Span Tower - Striving for Excellence"
              className="w-full h-auto max-h-24 object-contain"
              data-testid="sidebar-logo"
            />
            <p className="text-xs text-[#64748b] mt-3 text-center italic">Your Home. Connected.</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item, idx) => {
              if (item.section) {
                // Group separator: subtle uppercase heading between clusters
                // of related links. Keeps the sidebar scannable when there
                // are many entries (management view).
                return (
                  <div
                    key={`section-${idx}`}
                    className="pt-4 pb-1 px-4 text-[10px] font-semibold uppercase tracking-widest text-[#94a3b8]"
                    data-testid={`nav-section-${item.section.toLowerCase()}`}
                  >
                    {item.section}
                  </div>
                );
              }
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-[#2563eb] text-white'
                      : 'text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]'
                  }`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Profile & Logout */}
          <div className="p-4 border-t border-[#e2e8f0]">
            <div className="flex items-center gap-3 p-3 mb-3 bg-[#f8fafc] rounded-md">
              <div className="h-10 w-10 rounded-full bg-[#2563eb] flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-[#64748b] capitalize">{user?.role}</p>
              </div>
            </div>
            <InstallAppButton />
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start gap-2 text-[#64748b] hover:text-[#ef4444] hover:border-[#ef4444]"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 md:p-8 pb-24 md:pb-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav (only < md) */}
      <MobileBottomNav onMore={() => setSidebarOpen(true)} />

      {/* PWA install prompt (mobile-friendly, auto-hides after install/dismiss) */}
      <InstallPrompt />
    </div>
  );
};
